import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { branchAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage } from '../Common/admin';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NS = 'branch.create';

const BranchCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    branchCode: '',
    branchNameAr: '',
    branchNameEng: '',
    organizationId: '',
    countryId: '',
    areaId: '',
    cityId: '',
    districtId: '',
    streetName: '',
    crNumber: '',
    vatNumber: '',
    buildingNumber: '',
    additionalBuildingNumber: '',
    zipCode: '',
    telephoneNumber: '',
    email: '',
    vatRatio: '',
    isActive: true,
    userName: '',
    password: '',
  });
  const [organizations, setOrganizations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { countryId, areaId, cityId } = formData;

  useEffect(() => {
    Promise.all([branchAPI.getOrganizations(), branchAPI.getCountries()])
      .then(([orgRes, countryRes]) => {
        setOrganizations(orgRes.data || []);
        setCountries(countryRes.data || []);
      })
      .catch((err) => console.error('Failed to load dropdowns:', err));
  }, []);

  // Each level filters by its parent, and clears the levels below it.
  useEffect(() => {
    if (!countryId) {
      setAreas([]);
      return;
    }
    branchAPI
      .getAreas()
      .then((res) => {
        const filtered = (res.data || []).filter((a) => a.countryId === parseInt(countryId));
        setAreas(filtered);
        setFormData((prev) =>
          prev.areaId && !filtered.some((a) => a.id === parseInt(prev.areaId))
            ? { ...prev, areaId: '', cityId: '', districtId: '' }
            : prev
        );
      })
      .catch((err) => console.error('Failed to load areas:', err));
  }, [countryId]);

  useEffect(() => {
    if (!areaId) {
      setCities([]);
      return;
    }
    branchAPI
      .getCities()
      .then((res) => {
        const filtered = (res.data || []).filter((c) => c.areaId === parseInt(areaId));
        setCities(filtered);
        setFormData((prev) =>
          prev.cityId && !filtered.some((c) => c.id === parseInt(prev.cityId))
            ? { ...prev, cityId: '', districtId: '' }
            : prev
        );
      })
      .catch((err) => console.error('Failed to load cities:', err));
  }, [areaId]);

  useEffect(() => {
    if (!cityId) {
      setDistricts([]);
      return;
    }
    branchAPI
      .getDistricts()
      .then((res) => {
        const filtered = (res.data || []).filter((d) => d.cityId === parseInt(cityId));
        setDistricts(filtered);
        setFormData((prev) =>
          prev.districtId && !filtered.some((d) => d.id === parseInt(prev.districtId))
            ? { ...prev, districtId: '' }
            : prev
        );
      })
      .catch((err) => console.error('Failed to load districts:', err));
  }, [cityId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const e = {};
    [
      ['branchCode', 'branchCodeRequired'],
      ['branchNameAr', 'nameArRequired'],
      ['branchNameEng', 'nameEngRequired'],
      ['streetName', 'streetNameRequired'],
      ['telephoneNumber', 'telephoneNumberRequired'],
      ['userName', 'userNameRequired'],
    ].forEach(([f, key]) => {
      if (!String(formData[f]).trim()) e[f] = t(`${NS}.errors.${key}`);
    });

    [
      ['organizationId', 'organizationRequired'],
      ['countryId', 'countryRequired'],
      ['areaId', 'areaRequired'],
      ['cityId', 'cityRequired'],
      ['districtId', 'districtRequired'],
    ].forEach(([f, key]) => {
      if (!formData[f]) e[f] = t(`${NS}.errors.${key}`);
    });

    if (!formData.email.trim()) {
      e.email = t(`${NS}.errors.emailRequired`);
    } else if (!EMAIL_RE.test(formData.email)) {
      e.email = t(`${NS}.errors.emailInvalid`);
    }

    if (!formData.password.trim()) {
      e.password = t(`${NS}.errors.passwordRequired`);
    } else if (formData.password.length < 6) {
      e.password = t(`${NS}.errors.passwordMinLength`);
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const branch = {
        branchCode: formData.branchCode,
        branchNameAr: formData.branchNameAr,
        branchNameEng: formData.branchNameEng,
        organizationId: parseInt(formData.organizationId),
        countryId: parseInt(formData.countryId),
        areaId: parseInt(formData.areaId),
        cityId: parseInt(formData.cityId),
        districtId: parseInt(formData.districtId),
        streetName: formData.streetName,
        crNumber: formData.crNumber || null,
        vatNumber: formData.vatNumber || null,
        buildingNumber: formData.buildingNumber || null,
        additionalBuildingNumber: formData.additionalBuildingNumber || null,
        zipCode: formData.zipCode || null,
        telephoneNumber: formData.telephoneNumber,
        email: formData.email,
        vatRatio: formData.vatRatio ? parseFloat(formData.vatRatio) : null,
        isActive: formData.isActive,
      };

      await branchAPI.create({
        branch,
        userName: formData.userName,
        password: formData.password,
      });

      setShowSuccessModal(true);
      setTimeout(() => navigate('/branch'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t(`${NS}.messages.createError`));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const field = (name, labelKey, { type = 'text', required = false, maxLength, step } = {}) => (
    <div className="ad-form-field">
      <label className={`ad-label ${required ? 'ad-label-required' : ''}`} htmlFor={name}>
        {t(`${NS}.fields.${labelKey}`)}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        step={step}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const select = (name, labelKey, options, optionLabel, disabled = false) => (
    <div className="ad-form-field">
      <label className="ad-label ad-label-required" htmlFor={name}>
        {t(`${NS}.fields.${labelKey}`)}
      </label>
      <select
        id={name}
        name={name}
        className={`ad-select ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        disabled={disabled}
      >
        <option value="">{t(`${NS}.fields.${labelKey}`)}</option>
        {options.map((item) => (
          <option key={item.id} value={item.id}>
            {optionLabel(item)}
          </option>
        ))}
      </select>
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const backButton = (
    <button type="button" className="ad-btn" onClick={() => navigate('/branch')}>
      <i className="fas fa-arrow-right"></i>
      {t(`${NS}.backToList`)}
    </button>
  );

  return (
    <AdminPage title={t(`${NS}.title`)} headerActions={backButton}>
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {field('branchCode', 'branchCode', { required: true, maxLength: 20 })}
            {field('branchNameAr', 'nameAr', { required: true, maxLength: 200 })}
            {field('branchNameEng', 'nameEng', { required: true, maxLength: 200 })}
            {select('organizationId', 'organization', organizations, (o) => o.organizationNameEng)}
            {field('crNumber', 'crNumber', { maxLength: 50 })}
            {field('vatNumber', 'vatNumber', { maxLength: 50 })}
            {field('vatRatio', 'vatRatio', { type: 'number', step: '0.01' })}
          </div>

          <div className="ad-form-section">
            <h2 className="ad-form-section-title">{t(`${NS}.fields.streetName`)}</h2>
            <div className="ad-form-grid">
              {select('countryId', 'country', countries, (c) => c.nameEng)}
              {select('areaId', 'area', areas, (a) => a.areaNameEng, !countryId)}
              {select('cityId', 'city', cities, (c) => c.cityNameEng, !areaId)}
              {select('districtId', 'district', districts, (d) => d.districtNameEng, !cityId)}
              {field('streetName', 'streetName', { required: true, maxLength: 200 })}
              {field('buildingNumber', 'buildingNumber', { maxLength: 20 })}
              {field('additionalBuildingNumber', 'additionalBuildingNumber', { maxLength: 20 })}
              {field('zipCode', 'zipCode', { maxLength: 20 })}
            </div>
          </div>

          <div className="ad-form-section">
            <h2 className="ad-form-section-title">{t(`${NS}.fields.email`)}</h2>
            <div className="ad-form-grid">
              {field('telephoneNumber', 'telephoneNumber', { required: true, maxLength: 20 })}
              {field('email', 'email', { type: 'email', required: true, maxLength: 100 })}
            </div>
          </div>

          <div className="ad-form-section">
            <h2 className="ad-form-section-title">{t(`${NS}.fields.adminAccount`)}</h2>
            <div className="ad-form-grid">
              {field('userName', 'userName', { required: true, maxLength: 100 })}
              {field('password', 'password', { type: 'password', required: true })}
            </div>
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t(`${NS}.fields.isActive`)}</label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/branch')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t(`${NS}.save`)}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t(`${NS}.messages.createSuccess`)}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default BranchCreate;
